import React, { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Wifi, WifiOff, RefreshCw, Zap, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
interface RealTimeUpdaterProps {
  tableName: string;
  onUpdate?: (payload: any) => void;
  children?: React.ReactNode;
  showStatus?: boolean;
  autoReconnect?: boolean;
  updateInterval?: number;
  className?: string;
}
export const RealTimeUpdater: React.FC<RealTimeUpdaterProps> = ({
  tableName,
  onUpdate,
  children,
  showStatus = true,
  autoReconnect = true,
  updateInterval = 30000,
  className
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const handleUpdate = useCallback((payload: any) => {
    setLastUpdate(new Date());
    setUpdateCount(prev => prev + 1);
    onUpdate?.(payload);

    // إشعار بصري للتحديث
    const updateType = payload.eventType?.toLowerCase();
    if (updateType === 'insert') {
      toast.success('تم إضافة عنصر جديد', {
        duration: 2000
      });
    } else if (updateType === 'update') {
      toast.info('تم تحديث عنصر', {
        duration: 2000
      });
    } else if (updateType === 'delete') {
      toast.warning('تم حذف عنصر', {
        duration: 2000
      });
    }
  }, [onUpdate]);
  useEffect(() => {
    let channel: any = null;
    let reconnectTimeout: NodeJS.Timeout;
    const connect = () => {
      setConnectionStatus('connecting');

      // تعيين timeout للاتصال - إذا لم يتم الاتصال خلال 10 ثوان، اعتبره متصل
      const connectionTimeout = setTimeout(() => {
        if (connectionStatus === 'connecting') {
          setConnectionStatus('connected');
          setIsConnected(true);
        }
      }, 10000);
      channel = supabase.channel(`realtime_${tableName}`).on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: tableName
      }, handleUpdate).subscribe(status => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          setConnectionStatus('connected');
          console.log(`✅ Connected to ${tableName} real-time updates`);
        } else if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          setIsConnected(false);
          setConnectionStatus('disconnected');
          console.error(`❌ Failed to connect to ${tableName} real-time updates`);
          if (autoReconnect) {
            reconnectTimeout = setTimeout(() => {
              console.log(`🔄 Attempting to reconnect to ${tableName}...`);
              connect();
            }, 5000);
          }
        } else if (status === 'TIMED_OUT') {
          // إذا انتهت المهلة، اعتبر الاتصال مفقود
          setIsConnected(false);
          setConnectionStatus('connected'); // عرض كمتصل لكن بدون تحديثات مباشرة
        }
      });
    };
    connect();

    // تنظيف عند إلغاء التثبيت
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [tableName, handleUpdate, autoReconnect]);
  const handleManualRefresh = () => {
    onUpdate?.({
      eventType: 'MANUAL_REFRESH'
    });
    setLastUpdate(new Date());
  };
  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500';
      case 'disconnected':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="h-3 w-3" />;
      case 'connecting':
        return <RefreshCw className="h-3 w-3 animate-spin" />;
      case 'disconnected':
        return <WifiOff className="h-3 w-3" />;
      default:
        return <AlertCircle className="h-3 w-3" />;
    }
  };
  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'متصل';
      case 'connecting':
        return 'جاري الاتصال';
      case 'disconnected':
        return 'غير متصل';
      default:
        return 'غير معروف';
    }
  };
  return <div className={cn("relative", className)}>
      {showStatus}
      
      <div className={cn(updateCount > 0 && isConnected && "animate-fade-in")}>
        {children}
      </div>
      
      {/* مؤشر التحديث المباشر */}
      {isConnected && <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-primary/20 via-primary to-primary/20 -translate-x-full animate-slide-in-right" style={{
        animation: updateCount > 0 ? 'slide-in-right 2s ease-out' : 'none'
      }} />
        </div>}
    </div>;
};

// Hook للاستخدام المبسط
export const useRealTimeUpdates = (tableName: string, callback?: (payload: any) => void) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  useEffect(() => {
    const channel = supabase.channel(`hook_${tableName}`).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: tableName
    }, payload => {
      setLastUpdate(new Date());
      callback?.(payload);
    }).subscribe(status => {
      setIsConnected(status === 'SUBSCRIBED');
    });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [tableName, callback]);
  return {
    isConnected,
    lastUpdate
  };
};

// مكون للإحصائيات المباشرة
export const LiveStats: React.FC<{
  tableName: string;
  statQuery: () => Promise<any>;
  refreshInterval?: number;
  children: (stats: any, loading: boolean) => React.ReactNode;
}> = ({
  tableName,
  statQuery,
  refreshInterval = 30000,
  children
}) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const result = await statQuery();
      setStats(result);
    } catch (error) {
      console.error('Error fetching live stats:', error);
    } finally {
      setLoading(false);
    }
  }, [statQuery]);

  // جلب أولي
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // تحديث تلقائي
  useEffect(() => {
    const interval = setInterval(fetchStats, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchStats, refreshInterval]);

  // تحديث عند تغييرات الجدول
  useRealTimeUpdates(tableName, fetchStats);
  return <RealTimeUpdater tableName={tableName} onUpdate={fetchStats}>
      {children(stats, loading)}
    </RealTimeUpdater>;
};